import { useEffect, useRef } from 'react';
import { jobMonitoringService } from '../../../shared/services/jobMonitoringService';
import { videosService } from '../../../shared/services/videosService';
import { videoProcessingJobsService } from '../../../shared/services/videoProcessingJobsService';
import { CaptureItem, ToastState } from '../types';

interface UseJobMonitoringProps {
  orgId: string;
  files: CaptureItem[];
  selectedFolderPath: string;
  loadFolderContents: (folderPath: string) => Promise<void>;
  setToast: (toast: ToastState) => void;
}

/**
 * Custom hook for monitoring video processing jobs
 */
export const useJobMonitoring = ({
  orgId,
  files,
  selectedFolderPath,
  loadFolderContents,
  setToast,
}: UseJobMonitoringProps) => {
  const lastStatusRef = useRef<Record<string, 'idle' | 'running' | 'succeeded' | 'failed'>>({});

  useEffect(() => {
    lastStatusRef.current = {};
  }, [orgId]);

  useEffect(() => {
    if (!orgId) return;
    const filesNeedingMonitoring = files.filter(
      (f) => f.status === 'processing' || f.status === 'error'
    );
    if (filesNeedingMonitoring.length === 0) return;

    const videoIds = filesNeedingMonitoring
      .filter((f) => f.type === 'file' && f.videoId)
      .map((f) => f.videoId!)
      .filter(Boolean);

    if (videoIds.length === 0) return;

    // Initialize lastStatusRef with current job statuses to prevent showing toasts for existing failures
    const initializeStatuses = async () => {
      for (const videoId of videoIds) {
        const result = await videoProcessingJobsService.getJobsByVideoId(videoId);
        if (result.success && result.data) {
          const jobs = result.data;
          const hasFailed = jobMonitoringService.hasAnyJobFailed(jobs);
          const hasRunning = jobs.some((j) => j.status === 'running' || j.status === 'queued');
          const allComplete = jobMonitoringService.areAllJobsComplete(jobs);
          const hasJobs = jobs.length > 0;

          let initialStatus: 'idle' | 'running' | 'succeeded' | 'failed' = 'idle';
          if (hasFailed) {
            initialStatus = 'failed';
          } else if (hasRunning) {
            initialStatus = 'running';
          } else if (hasJobs && allComplete) {
            initialStatus = 'succeeded';
          }

          // Set initial status so we don't show toast for existing failures
          lastStatusRef.current[videoId] = initialStatus;
        }
      }
    };

    initializeStatuses();

    // Start monitoring jobs for all videos
    const cleanup = jobMonitoringService.startBatchMonitoring(
      videoIds,
      (videoId, jobs) => {
        const allComplete = jobMonitoringService.areAllJobsComplete(jobs);
        const hasFailed = jobMonitoringService.hasAnyJobFailed(jobs);
        const hasRunning = jobs.some((j) => j.status === 'running' || j.status === 'queued');
        const hasJobs = jobs.length > 0;

        let nextStatus: 'idle' | 'running' | 'succeeded' | 'failed' = 'idle';
        if (hasFailed) {
          nextStatus = 'failed';
        } else if (hasRunning) {
          nextStatus = 'running';
        } else if (hasJobs && allComplete) {
          nextStatus = 'succeeded';
        }

        const previousStatus = lastStatusRef.current[videoId];
        if (previousStatus === nextStatus) {
          return;
        }
        lastStatusRef.current[videoId] = nextStatus;

        const shouldRefresh =
          nextStatus === 'running' ||
          nextStatus === 'failed' ||
          (nextStatus === 'succeeded' && previousStatus === 'running');

        // Find the video in files
        const fileItem = files.find((f) => f.videoId === videoId);
        if (fileItem) {
          if (nextStatus === 'succeeded') {
            videosService.updateVideo(videoId, { status: 'ready' }).catch(console.error);
          } else if (nextStatus === 'failed') {
            videosService.updateVideo(videoId, { status: 'error' }).catch(console.error);
          } else if (nextStatus === 'running') {
            videosService.updateVideo(videoId, { status: 'processing' }).catch(console.error);
          }

          // Only show toast on status transitions, not on initial load
          if (nextStatus === 'succeeded' && previousStatus === 'running') {
            setToast({
              isVisible: true,
              type: 'success',
              message: `Processing complete for ${fileItem.name}`,
            });
          } else if (nextStatus === 'failed' && previousStatus !== 'failed' && previousStatus !== undefined) {
              setToast({
                isVisible: true,
                type: 'error',
                message: `Processing failed for ${fileItem.name}`
              });
          }
        }

        if (shouldRefresh) {
          loadFolderContents(selectedFolderPath);
        }
      },
      5000 // Poll every 5 seconds
    );

    return cleanup;
  }, [orgId, files, selectedFolderPath, loadFolderContents, setToast]);
};

